import { Directionality } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import {
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';

import { PLACEMENT, WINDOW } from '@ideals/types';

import { getTooltipPositions } from './tooltip-positions';
import { TooltipPresenter } from './tooltip-presenter';

const DEFAULT_PLACEMENT_BEHAVIOUR: PLACEMENT[] = [PLACEMENT.Bottom, PLACEMENT.Right, PLACEMENT.Left];
const DEFAULT_SHOW_DELAY = 500;
const SCROLL_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

@Directive({
  exportAs: 'idealsTooltip',
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
  selector: '[ideals-tooltip], [idealsTooltip]',
})
export class TooltipDirective implements OnInit, OnDestroy {
  readonly content = input<string | null | undefined>('', { alias: 'ideals-tooltip' });
  readonly disabled = input(false);
  readonly hideDelay = input(0);
  readonly html = input(false, { alias: 'tooltipHTML' });
  readonly placement = input<PLACEMENT>(PLACEMENT.Bottom);
  readonly placementBehaviour = input<PLACEMENT[]>(DEFAULT_PLACEMENT_BEHAVIOUR);
  readonly showDelay = input(DEFAULT_SHOW_DELAY);
  readonly tooltipWidthAuto = input(false);

  readonly #directionality = inject(Directionality);
  readonly #element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  readonly #hideOnScroll = (): void => {
    this.#hideNow();
  };
  readonly #injector = inject(Injector);
  readonly #isDisabled = computed(() => this.disabled() || this.#overflowDisabled());
  readonly #overflowDisabled = signal(false);
  readonly #presenter = new TooltipPresenter(this.#element, inject(Overlay));
  readonly #window = inject(WINDOW);

  #hideTimeout?: ReturnType<typeof setTimeout>;
  #showTimeout?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.#effects();
  }

  ngOnDestroy(): void {
    this.#hideNow();
  }

  onMouseEnter(): void {
    if (this.#isDisabled() || !this.content()) {
      return;
    }

    this.#clearTimeouts();
    this.#window.addEventListener('scroll', this.#hideOnScroll, SCROLL_LISTENER_OPTIONS);
    this.#showTimeout = setTimeout(() => this.#show(), this.showDelay());
  }

  onMouseLeave(): void {
    this.#removeScrollListener();
    this.#clearTimeouts();
    this.#hideTimeout = setTimeout(() => this.#presenter.hide(), this.hideDelay());
  }

  setOverflowDisabled(value: boolean): void {
    this.#overflowDisabled.set(value);
  }

  #clearTimeouts(): void {
    clearTimeout(this.#hideTimeout);
    clearTimeout(this.#showTimeout);
  }

  #effects(): void {
    effect(() => {
      if (this.#isDisabled()) {
        this.#hideNow();
      }
    }, { injector: this.#injector });

    effect(() => {
      this.#syncOpenTooltip(this.content());
    }, { injector: this.#injector });
  }

  #hideNow(): void {
    this.#clearTimeouts();
    this.#removeScrollListener();
    this.#presenter.hide();
  }

  #removeScrollListener(): void {
    this.#window.removeEventListener('scroll', this.#hideOnScroll, SCROLL_LISTENER_OPTIONS);
  }

  #show(): void {
    this.#presenter.show({
      content: this.content() ?? '',
      html: this.html(),
      positions: getTooltipPositions(this.placement(), this.placementBehaviour(), this.#directionality.value),
      widthAuto: this.tooltipWidthAuto(),
    });
  }

  #syncOpenTooltip(content: string | null | undefined): void {
    if (!this.#presenter.isOpen) {
      return;
    }

    if (content) {
      this.#presenter.update(content);
    } else {
      this.#hideNow();
    }
  }
}
